import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { specials } from "@/mocks/specials";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import type { Movie } from "@/types/content";

export default function Special() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get("id") || specials[0].id;
  const active = specials.find((s) => s.id === activeId) || specials[0];
  const [activeGroup, setActiveGroup] = useState(0);

  const pick = (id: string) => {
    setSearchParams({ id });
    setActiveGroup(0);
  };

  const groupMovies = (names: string[]): Movie[] =>
    names
      .map((name) => movies.find((m) => m.title === name))
      .filter((m): m is Movie => Boolean(m));

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "专题合集", to: "/special" }, { label: active.title }]} />

      <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar">
        {specials.map((sp) => (
          <button
            key={sp.id}
            type="button"
            onClick={() => pick(sp.id)}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              sp.id === active.id
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            {sp.title}
          </button>
        ))}
      </div>

      <section className="relative mt-5 overflow-hidden rounded-lg">
        <div className="relative h-64 w-full md:h-80">
          <img
            src={active.cover}
            alt={`${active.title}专题封面`}
            title={`${active.title} - 激情视频专题`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
          <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
            <span className="inline-flex w-fit items-center gap-1 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className="ri-bookmark-3-line" />
              精选专题
            </span>
            <h1 className="mt-3 text-2xl font-black text-background-50 md:text-4xl">{active.title}</h1>
            <p className="mt-2 max-w-xl text-sm text-background-200 md:text-base">{active.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="text-lg font-bold text-foreground-950 md:text-xl">专题导语</h2>
          <p className="mt-3 text-sm leading-8 text-foreground-700 md:text-base">{active.intro}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {active.groups.map((group, i) => (
              <button
                key={group.name}
                type="button"
                onClick={() => setActiveGroup(i)}
                className={`whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  i === activeGroup
                    ? "border-secondary-500 bg-secondary-500 text-background-50"
                    : "border-background-200 bg-background-50 text-foreground-700 hover:border-secondary-300"
                }`}
              >
                {group.name}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <p className="text-sm text-foreground-500">
              {active.groups[activeGroup]?.desc}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {groupMovies(active.groups[activeGroup]?.movies || []).map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-lightbulb-line text-accent-600" />
              推荐理由
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">{active.reason}</p>
          </div>

          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-bold text-foreground-950">相关专题</h3>
            <ul className="mt-3 space-y-3">
              {specials
                .filter((s) => s.id !== active.id)
                .map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => pick(s.id)}
                      className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-background-100 cursor-pointer"
                    >
                      <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={s.cover}
                          alt={`${s.title}封面`}
                          title={`${s.title} - 激情视频专题`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground-900">{s.title}</span>
                        <span className="block truncate text-xs text-foreground-500">{s.subtitle}</span>
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}